import { isEmpty } from 'lodash';
import { FC, useCallback, useEffect, useState } from 'react';

import { InAppContentDefinition } from 'modules/graphql/types';
import { useGetTemplateUserJsonTestData } from 'modules/templateEditor/hooks/useGetTemplateUserJsonTestData';

import { PreviewWithDataErrorMessage } from '../../common/types';
import {
  getContentWithData,
  getContentWithDataAndMergeParameters,
  getPreviewWithDataError,
} from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { usePreviewWithDataContext } from '../../context/PreviewWithDataContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { IN_APP_VIEW_OPTIONS } from '../constants';
import { InAppPreviewContext, InAppPreviewContextState } from './InAppPreviewContext';

export const InAppPreviewContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { liveTemplate } = useEditableFieldsContext();
  const { canRefreshPreview, dataFeedData, userData, isUserDataDirty, setIsUserDataDirty } =
    usePreviewWithDataContext();
  const { templateId } = useQueryParamContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messageHtml, setMessageHtml] = useState<string>('');
  const [messageInboxSubtitle, setMessageInboxSubtitle] = useState<string>('');
  const [messageInboxTitle, setMessageInboxTitle] = useState<string>('');
  const [messageRawDataPayload, setMessageRawDataPayload] = useState<string>('{}');
  const [messageRokuHeader, setMessageRokuHeader] = useState<string>('');
  const [messageRokuBody, setMessageRokuBody] = useState<string>('');
  const [previewViewOption, setPreviewViewOption] = useState<IN_APP_VIEW_OPTIONS>(
    IN_APP_VIEW_OPTIONS.inApp,
  );
  const [previewError, setPreviewError] = useState<PreviewWithDataErrorMessage | undefined>(
    undefined,
  );
  const inAppData = (liveTemplate?.mediumData || {}) as InAppContentDefinition;
  const hasContent = !isEmpty(inAppData);
  const { html: contentHtml, payload } = inAppData;
  const contentInboxSubtitle = inAppData.inboxMetadata?.subtitle || '';
  const contentInboxTitle = inAppData.inboxMetadata?.title || '';
  const rokuDialogHeader = inAppData.ottPayload?.dialogHeader || '';
  const rokuDialogBody = inAppData.ottPayload?.dialogText || '';
  const locale = inAppData?.locale?.name || '';

  const { getTemplateUserJsonTestData } = useGetTemplateUserJsonTestData();

  const onRefreshPreview = useCallback(async () => {
    // Only make request when template data has been received
    if (canRefreshPreview && hasContent) {
      setIsLoading(true);

      const userDataToSave = await getTemplateUserJsonTestData(userData, isUserDataDirty);

      getContentWithData(
        templateId,
        {
          subject: '',
          inAppInboxSubtitle: contentInboxSubtitle ?? '',
          inAppInboxTitle: contentInboxTitle ?? '',
          payload: JSON.parse(payload ?? '{}'),
          rokuDialogBody,
          rokuDialogHeader,
        },
        JSON.parse(dataFeedData),
        JSON.parse(userDataToSave),
      )
        .then(({ data }) => {
          setIsUserDataDirty(false);
          setMessageInboxSubtitle(data.inAppInboxSubtitle);
          setMessageInboxTitle(data.inAppInboxTitle);
          setMessageRawDataPayload(JSON.stringify(data.payload));
          setMessageRokuHeader(data.rokuDialogHeader);
          setMessageRokuBody(data.rokuDialogBody);
        })
        .catch(() => {
          /* preview error doesn't actually get returned at this endpoint for this medium */
        })
        .finally(() => {
          getContentWithDataAndMergeParameters(templateId, locale)
            .then(({ data }) => {
              setMessageHtml(data);
              setPreviewError(undefined);
            })
            .catch(({ response }) => {
              setPreviewError(getPreviewWithDataError(response?.data?.message || ''));
            })
            .finally(() => setIsLoading(false));
        });
    }
  }, [
    canRefreshPreview,
    dataFeedData,
    contentHtml,
    templateId,
    userData,
    locale,
    payload,
    rokuDialogBody,
    rokuDialogHeader,
    hasContent,
    isUserDataDirty,
    setIsUserDataDirty,
    getTemplateUserJsonTestData,
  ]);

  /* Note: we only want this useEffect to run when dataFeedData, userData, or locale changes even though
   * it is also dependent on onRefreshPreview. However, onRefreshPreview shares ALL of the useEffect's
   * dependencies, so the version of onRefreshPreview used will always be current.
   * Including onRefreshPreview as a dependency here would cause an unintended side-effect of
   * running onRefreshPreview on each change of fullSmsMessage */
  useEffect(() => {
    if (canRefreshPreview) {
      onRefreshPreview();
    }
  }, [canRefreshPreview, dataFeedData, userData, locale, rokuDialogBody, rokuDialogHeader]);

  const context: InAppPreviewContextState = {
    isLoading,
    messageHtml,
    messageInboxSubtitle,
    messageInboxTitle,
    messageRawDataPayload,
    messageRokuHeader,
    messageRokuBody,
    onRefreshPreview,
    previewError,
    previewViewOption,
    setPreviewViewOption,
  };

  return <InAppPreviewContext.Provider value={context}>{children}</InAppPreviewContext.Provider>;
};
